// src/modules/requerimiento/hooks/useVerificacionReporte.js
import { useCallback, useEffect, useRef, useState } from "react";
import { verificarReporte } from "../utils/reporteModem";

// Cuántos equipos se verifican a la vez (cada uno consulta 5 servicios).
const VERIFICAR_EN_PARALELO = 3;

/** Clave de un equipo en `reportes`. */
export const claveReporte = (m) => m?.num_sim || m?.emai;

/**
 * Estado "¿reporta?" de una lista de equipos (Revisión en Sistemas de adición y mantenimiento).
 * reportes: clave -> "verificando" | resultado de verificarReporte. Al desmontar se corta la
 * verificación en curso.
 */
export default function useVerificacionReporte() {
  const [reportes, setReportes] = useState({});
  const verificacionRef = useRef(0);

  const verificarUno = useCallback(async (m) => {
    const clave = claveReporte(m);
    if (!clave) return;
    setReportes((prev) => ({ ...prev, [clave]: "verificando" }));
    let res;
    try {
      res = await verificarReporte({ num_sim: m.num_sim, emai: m.emai });
    } catch {
      res = { estado: "no_se_pudo", plataformas: [] };
    }
    setReportes((prev) => ({ ...prev, [clave]: res }));
  }, []);

  const verificarTodos = useCallback(async (lista) => {
    const id = ++verificacionRef.current;
    const pendientes = (lista || []).filter(claveReporte);
    setReportes(Object.fromEntries(pendientes.map((m) => [claveReporte(m), "verificando"])));
    let i = 0;
    const trabajador = async () => {
      while (i < pendientes.length && id === verificacionRef.current) {
        const m = pendientes[i++];
        await verificarUno(m);
      }
    };
    await Promise.all(Array.from({ length: VERIFICAR_EN_PARALELO }, trabajador));
  }, [verificarUno]);

  useEffect(() => () => { verificacionRef.current += 1; }, []);

  return { reportes, verificarUno, verificarTodos };
}

/** Resumen de una lista: verificando, reportando (con datos recientes), desactualizados y sin reporte. */
export function contarReportes(lista, reportes) {
  const estados = (lista || []).map((m) => reportes[claveReporte(m)]);
  return {
    verificando: estados.some((e) => !e || e === "verificando"),
    reportando: estados.filter((e) => e?.estado === "reporta" && !e.desactualizado).length,
    desactualizados: estados.filter((e) => e?.estado === "reporta" && e.desactualizado).length,
    sinReporte: estados.filter((e) => e?.estado === "no_reporta").length,
  };
}
